import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Spinner, Card } from 'flowbite-react';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { usePerson } from '../../hooks/usePerson';
import EditableTitle from '../../components/EditableTitle';
import Avatar from '../../components/common/Avatar';
import RemoteReferenceIcon from '../../components/RemoteReferenceIcon';
import WorkMatchList from '../../components/schedule/WorkMatchList';
import WorkMatchPairForActor from '../../components/schedule/WorkMatchPairForActor';
import AddPersonToTeamForm from '../../components/teams/AddPersonToTeamForm';
import ListWithActions from '../../components/common/ListWithActions';
import { FiUsers, FiMinusCircle, FiCalendar } from 'react-icons/fi';
import AvailabilityPolicyForm from '../../components/forms/AvailabilityPolicyForm';
import AvailabilityList from '../../components/availability/AvailabilityList';
import { useSchedule } from '../../hooks/useSchedule';
import { D3GanttChart, D3GanttItem } from '../../components/charts/D3GanttChart';
import { useAsync } from 'react-async-hook';


const PersonDetailPage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const { person, loading, error } = usePerson(personId);
  const { applyCommand, workspaceId, database } = useDatabase();
  const { schedule } = useSchedule();
  const gantt = useAsync(async () => {
    if (!schedule || !database || !person) return { items: [] as D3GanttItem[], epochs: [] as { id: string; label: string }[] };

    // The schedule is now a fuzzy schedule DTO (plain object). This gantt
    // was built for the old epoch-based schedule. Return empty until this
    // is rewritten to use fuzzy schedule data.
    if (typeof (schedule as any).allocations_by_task !== 'function') {
      return { items: [] as D3GanttItem[], epochs: [] as { id: string; label: string }[] };
    }

    const byTask = schedule.allocations_by_task();
    const tasks = database.tasks();
    const allEpochs = schedule.ordered_epochs();

    const epochToRangeMs = (epoch: any): { start: number; end: number } | null => {
      const e = (typeof epoch.inner === 'function') ? epoch.inner() : epoch;
      if (e && e.type === 'Day' && typeof e.day === 'string') {
        const start = new Date(e.day + 'T00:00:00Z').getTime();
        const end = start + 24*60*60*1000;
        return { start, end };
      }
      if (e && e.type === 'Days' && typeof e.start_inclusive === 'string' && typeof e.end_inclusive === 'string') {
        const start = new Date(e.start_inclusive + 'T00:00:00Z').getTime();
        const end = new Date(e.end_inclusive + 'T00:00:00Z').getTime() + 24*60*60*1000;
        return { start, end };
      }
      return null;
    };

    // Build future epoch list and lookup map (index by normalized key)
    const nowMs = Date.now();
    const futureEpochs: { id: string; label: string; startMs: number; endMs: number; key: string }[] = [];
    const epochKey = (inner: any): string => {
      if (!inner) return '';
      if (inner.type === 'Day') return `D:${inner.day}`;
      if (inner.type === 'Days') return `R:${inner.start_inclusive}:${inner.end_inclusive}`;
      return '';
    };
    for (const w of allEpochs) {
      const e = w.inner();
      const rng = epochToRangeMs(e);
      if (!rng) continue;
      if (rng.end < nowMs) continue; // future only
      const key = epochKey(e);
      const label = (e.type === 'Day') ? new Date(rng.start).toLocaleDateString(undefined,{ month:'numeric', day:'numeric'}) : `${new Date(rng.start).toLocaleDateString(undefined,{ month:'numeric', day:'numeric'})}`;
      futureEpochs.push({ id: key || String(futureEpochs.length), label, startMs: rng.start, endMs: rng.end, key });
    }
    // Limit to next N epochs for readability
    const MAX_EPOCHS = 26;
    const windowEpochs = futureEpochs.slice(0, MAX_EPOCHS);
    const epochs = windowEpochs.map(e => ({ id: e.id, label: e.label }));
    const indexByKey = new Map<string, number>();
    windowEpochs.forEach((e, i) => indexByKey.set(e.key, i));

    // Compute 'today' position within the current epoch (fractional index)
    let currentEpochIndex: number | undefined = undefined;
    let currentEpochPosition: number | undefined = undefined;
    const cur = schedule.current_epoch();
    if (cur) {
      const curInner = cur.inner();
      const curRng = epochToRangeMs(curInner);
      if (curRng) {
        currentEpochIndex = windowEpochs.findIndex(e => curRng.start >= e.startMs && curRng.start < e.endMs);
        if (currentEpochIndex >= 0) {
          const e = windowEpochs[currentEpochIndex];
          const frac = Math.min(1, Math.max(0, (nowMs - e.startMs) / (e.endMs - e.startMs)));
          currentEpochPosition = currentEpochIndex + frac;
        }
      }
    }

    const items: D3GanttItem[] = [];
    for (const taskId of Object.keys(byTask)) {
      const allocs = byTask[taskId] || [];
      if (allocs.length === 0) continue;
      const relevant = allocs.filter(a => a.person_id() === person.id);
      if (relevant.length === 0) continue;

      const ranges: { start: number; end: number }[] = [];
      for (const a of relevant) {
        const r = epochToRangeMs(a.when());
        if (r) ranges.push(r);
      }
      ranges.sort((a,b)=>a.start-b.start);
      const merged: { start: number; end: number }[] = [];
      for (const r of ranges) {
        if (merged.length === 0) { merged.push({ ...r }); continue; }
        const last = merged[merged.length-1];
        if (r.start <= last.end) last.end = Math.max(last.end, r.end); else merged.push({ ...r });
      }

      const title = tasks[taskId] ? tasks[taskId].title() : taskId;
      const href = `/workspaces/${workspaceId}/tasks/${taskId}`;
      merged.forEach((span, idx) => {
        if (span.end <= nowMs) return; // future only
        // Snap to epoch indices using keys
        const sKey = windowEpochs.find(e => span.start >= e.startMs && span.start < e.endMs)?.key
          ?? windowEpochs.find(e => e.startMs >= span.start)?.key;
        let eKey = windowEpochs.find(e => span.end > e.startMs && span.end <= e.endMs)?.key;
        if (!eKey) {
          for (let i = windowEpochs.length - 1; i >= 0; i--) {
            if (windowEpochs[i].endMs <= span.end) { eKey = windowEpochs[i].key; break; }
          }
        }
        if (!eKey && windowEpochs.length > 0) eKey = windowEpochs[windowEpochs.length - 1].key;
        if (!sKey || !eKey) return;
        const sIdx = indexByKey.get(sKey);
        const eIdx = indexByKey.get(eKey);
        if (sIdx == null || eIdx == null) return;
        items.push({
          id: `${taskId}-${idx}`,
          label: title,
          // In categorical mode we carry epoch indices inside Date.valueOf()
          startDate: new Date(sIdx),
          endDate: new Date(eIdx),
          href,
        });
      });
    }

    // Build milestones from outcome schedule (each gets its own row)
    const milestones: { epochIndex: number; lane?: number; label: string; href?: string }[] = [];
    if (typeof schedule.outcome_schedule === 'function') {
      const outcomeSchedule: Record<string, any> = schedule.outcome_schedule() as any;
      const outcomes = database.outcomes();
      let laneCounter = 0;
      for (const outcomeId of Object.keys(outcomeSchedule)) {
        const os = outcomeSchedule[outcomeId];
        if (!os || !os.completion_epoch) continue;
        const ce = os.completion_epoch;
        if (ce.type !== 'Scheduled') continue;
        const epoch = ce.epoch;
        const rng = epochToRangeMs(epoch);
        if (!rng) continue;
        // map epoch to index in windowEpochs
        const idx = windowEpochs.findIndex(e => rng.start >= e.startMs && rng.start < e.endMs);
        if (idx < 0) continue;
        const title = outcomes[outcomeId]?.title?.() ?? 'Outcome';
        const href = `/workspaces/${workspaceId}/outcomes/${outcomeId}`;
        milestones.push({ epochIndex: idx, lane: laneCounter++, label: title, href });
      }
    }

    return { items, epochs, milestones, currentEpochIndex, currentEpochPosition } as any;
  }, [schedule, database, person, workspaceId]);

  // Function to update the person name
  const updatePersonName = async (newName: string) => {
    if (!person || !workspaceId) return;

    try {
      // Create command with proper typing
      const command: wasm.DatabaseCommand = {
        type: "UpdatePerson",
        id: person.id,
        actions: [
          {
            type: "SetName",
            name: newName,
          },
        ],
      };

      await applyCommand(command);
    } catch (error) {
      console.error('Error updating person name:', error);
      throw error; // Let the EditableTitle component handle the error
    }
  };

  // Remove person from a team
  const handleRemoveFromTeam = async (teamId: string) => {
    if (!person) return;
    try {
      const command: wasm.DatabaseCommand = {
        type: 'SetTeamMembership',
        membership: {
          member: { Person: person.id },
          team: teamId,
        },
        enabled: false,
      };
      await applyCommand(command);
    } catch (error) {
      console.error('Error removing from team:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-6">
        <Spinner size="xl" />
      </div>
    );
  }

  // Error state
  if (error || !person) {
    return (
      <div className="p-6">
        <div className="text-red-500 mb-4">
          {error || 'Person not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center mb-6 gap-4">
        <div className="flex-shrink-0">
          <Avatar
            imageUrl={person.avatarUrl}
            name={person.name}
            size="lg"
            showTooltip={false}
          />
        </div>
        <EditableTitle
          title={person.name}
          onTitleChange={updatePersonName}
          size="lg"
          className="flex-grow"
        />
      </div>

      {/* Remote References */}
      {person.remoteReferences.length > 0 && (
        <Card className="mb-6">
          <div className="prose dark:prose-invert max-w-none">
            <h3 className="text-lg font-medium mb-4">External References</h3>
            <div className="flex flex-wrap gap-3">
              {person.remoteReferences.map((reference, index) => (
                <div key={index} className="flex items-center gap-2 border border-cm-border-subtle rounded-lg p-2">
                  <RemoteReferenceIcon
                    reference={reference}
                    showLabel={true}
                  />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Work Matching */}
      <Card className="mb-6">
        <div className="prose dark:prose-invert max-w-none">
          <h3 className="text-lg font-medium mb-4">Work Matching</h3>
        </div>
        <div className="p-2">
          <WorkMatchList type="actor" actor={{ kind: 'Person', id: person.id }} />
          <WorkMatchPairForActor
            actors={[
              { kind: 'Person', id: person.id },
              ...((person.teamIds || []).map((id) => ({ kind: 'Team' as const, id })))
            ]}
          />
        </div>
      </Card>

      {/* Teams */}
      <Card className="mb-6">
        <div className="prose dark:prose-invert max-w-none">
          <h3 className="text-lg font-medium mb-4">Teams</h3>
          {(() => {
            const entries = (person.teamIds || [])
              .map((id) => {
                const team = database ? database.teams()[id] : undefined;
                const name = team ? team.name() : id;
                return { id, name };
              })
              .sort((a, b) => a.name.localeCompare(b.name));

            if (entries.length === 0) {
              return <p className="text-cm-text-muted mb-3">No teams</p>;
            }

            const items = entries.map(({ id, name }) => ({
              key: id,
              left: (
                <Link to={`/workspaces/${workspaceId}/teams/${id}`} className="flex items-center gap-3 group">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600">
                    <FiUsers />
                  </div>
                  <span className="group-hover:underline">{name}</span>
                </Link>
              ),
              right: (
                <button
                  className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-cm-bg-hover"
                  aria-label="Remove from team"
                  onClick={() => {
                    if (confirm('Remove this person from the team?')) {
                      handleRemoveFromTeam(id);
                    }
                  }}
                >
                  <FiMinusCircle className="w-4 h-4" />
                </button>
              )
            }));

            return <ListWithActions items={items} className="mb-3" />;
          })()}

          {/* Add to team */}
          <div className="mt-1">
            <AddPersonToTeamForm
              personId={person.id}
              onSubmit={applyCommand}
            />
          </div>
        </div>
      </Card>

      {/* Schedule */}
      <Card className="mb-6">
        <div className="prose dark:prose-invert max-w-none">
          <h3 className="text-lg font-medium mb-4">Schedule</h3>
        </div>
        <div className="p-2">
          {(() => {
            const result = gantt.result ?? { items: [], epochs: [], milestones: [], currentEpochIndex: undefined, currentEpochPosition: undefined } as any;
            const items = result.items;
            const epochs = result.epochs;
            const milestones = result.milestones || [];
            const currentEpochIndex = result.currentEpochIndex;
            const currentEpochPosition = result.currentEpochPosition;
            return (
              <D3GanttChart
                items={items}
                title="Schedule Timeline"
                description={items.length === 0 ? 'No scheduled items for this person.' : ''}
                laneHeight={36}
                legendMode="none"
                showItemLabelInBar={true}
                epochs={epochs}
                epochWidthPx={140}
                milestones={milestones}
                currentEpochIndex={currentEpochIndex}
                currentEpochPosition={currentEpochPosition}
                oneItemPerRow={true}
                tooltipRender={(it) => (
                  <div className="px-2 py-1 rounded bg-black/80 text-white text-xs shadow">
                    <div className="font-medium">{it.label}</div>
                  </div>
                )}
              />
            );
          })()}
        </div>
      </Card>

      {/* Availability */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="w-5 h-5 text-cm-accent-blue" />
          <h3 className="text-lg font-mono font-medium text-cm-text-primary">Availability</h3>
        </div>
        <p className="text-xs text-cm-text-muted mb-3">
          Individual policies take highest precedence, then team, then workspace defaults. First matching policy for a day wins.
        </p>
        {(() => {
          const wrapped = database ? database.people()[person.id] : undefined;
          const avs = wrapped ? wrapped.availability() : [];
          const items = avs
            .filter((a: wasm.PersonAvailability) => !!a.id)
            .map((a: wasm.PersonAvailability) => ({ id: a.id as string, policy: a.policy, after: a.after, before: a.before }));

          const onDelete = async (id: string) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdatePerson',
              id: person.id,
              actions: [
                { type: 'UpdateAvailability', action: { Delete: { id } } },
              ],
            };
            await applyCommand(command);
          };

          const onReorder = async ({ id, before }: { id: string; before: string | null }) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdatePerson',
              id: person.id,
              actions: [
                { type: 'UpdateAvailability', action: { Reorder: { id, before } } },
              ],
            };
            await applyCommand(command);
          };

          return (
            <div className="mb-4">
              <AvailabilityList items={items} onDelete={onDelete} onReorder={onReorder} emptyText="No personal availability policies yet." />
            </div>
          );
        })()}
        <AvailabilityPolicyForm
          onSubmit={async (availability) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdatePerson',
              id: person.id,
              actions: [
                { type: 'UpdateAvailability', action: { Upsert: { availability } } },
              ],
            };
            await applyCommand(command);
          }}
        />
      </Card>

    </div>
  );
};

export default PersonDetailPage;
